import React, { createContext, useContext, type ReactNode } from 'react';

// Simplified context type (English-only)
interface LanguageContextType {
  language: 'en';
  setLanguage: (lang: 'en') => void;
  t: (key: string) => string;
}

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// English-only translations
const translations = {
  // Header
  'header.services': 'Services',
  'header.pricing': 'Pricing', 
  'header.about': 'About',
  'header.book': 'Book',
  'header.call': 'Call',
  
  // Hero
  'hero.headline': 'English-Speaking Doctor in Brazil — Online Telemedicine',
  'hero.subheadline': 'Board-certified physician offering online medical consultations throughout Brazil. Prescriptions issued only when clinically indicated after live evaluation.',
  'hero.cta1': 'Book Online Consultation',
  'hero.cta2': 'View Pricing',
  'hero.microcopy': 'Average reply in 30 minutes · Daily 07:00–22:00 BRT via WhatsApp',

  // Differentials
  'differentials.headline': 'Why Choose Our Medical Care?',
  'differentials.subheadline': 'Professional healthcare that understands your needs',
  'differentials.licensed.title': 'Brazilian Licensed Doctor',
  'differentials.licensed.description': 'Licensed physician with full legal authorization to practice medicine and prescribe medications in Brazil',
  'differentials.availability.title': 'Daily Availability',
  'differentials.availability.description': 'Medical support available daily 7:00-22:00 for consultations and health concerns',
  'differentials.bilingual.title': 'Fluent English & Portuguese',
  'differentials.bilingual.description': 'Clear communication in your preferred language, ensuring accurate diagnosis and treatment understanding',
  'differentials.insurance.title': 'Insurance Receipts',
  'differentials.insurance.description': 'Detailed medical receipts provided for international insurance claims and reimbursement requests',
  'differentials.experience.title': '5+ Years Experience', 
  'differentials.experience.description': 'Extensive experience treating international patients, tourists, and expatriates throughout Brazil',
  'differentials.telemedicine.title': 'Telemedicine Certified',
  'differentials.telemedicine.description': 'Fully compliant with Brazilian CFM regulations for online medical consultations and prescriptions',

  // How it works
  'how.headline': 'How It Works',
  'how.subheadline': 'Simple steps to get the medical care you need',
  'how.step1.title': 'Book Your Appointment',
  'how.step1.description': 'Contact us via WhatsApp or our booking form. Choose an online consultation for adults or children and pick a time that suits you.',
  'how.step2.title': 'Medical Consultation', 
  'how.step2.description': 'Receive professional medical care from our licensed Brazilian doctor in English or Portuguese.',
  'how.step3.title': 'Treatment & Follow-up',
  'how.step3.description': 'Get prescriptions, medical certificates, and follow-up care as needed. Insurance receipts provided.',

  // Services
  'services.headline': 'Our Medical Services',
  'services.subheadline': 'Comprehensive healthcare solutions for your needs in Brazil',

  // About
  'about.headline': 'About Rio Med Care',
  'about.subheadline': 'English-speaking medical care across Brazil',
  'about.description': 'Rio Med Care provides English-speaking telemedicine throughout Brazil. Our licensed Brazilian physicians deliver clinical care for international patients, tourists and expatriates.',

  // Pricing
  'pricing.headline': 'Transparent Pricing',
  'pricing.subheadline': 'No hidden fees. Professional medical care at competitive rates.',

  // Testimonials
  'testimonials.headline': 'What Our Patients Say',
  'testimonials.subheadline': 'Real experiences from satisfied patients',

  // Contact
  'contact.headline': 'Get In Touch',
  'contact.subheadline': 'Ready to book your consultation? Contact us now.',

  // FAQ
  'faq.headline': 'Frequently Asked Questions',
  'faq.subheadline': 'Common questions about our medical services',

  // Footer
  'footer.tagline': 'Professional English-speaking medical care in Brazil',
  'footer.rights': 'All rights reserved.',
  'footer.services': 'Services',
  'footer.online': 'Online Consultations',
  'footer.prescriptions': 'Prescriptions',
  'footer.company': 'Company',
  'footer.about': 'About Us',
  'footer.contact': 'Contact',
  'footer.legal': 'Legal',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service'
};

// Language Provider component (English-only)
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const language: 'en' = 'en';
  const setLanguage = (lang: 'en') => {
    // English-only site, no language switching needed
  };

  const t = (key: string): string => {
    return translations[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};